import { Box, Button, CircularProgress, Typography } from '@mui/material';
import Dialog from 'components/dialog';
import RadioSelectItem from 'components/radio-select-item';
import Select from 'components/select';
import TextField from 'components/text-field';

import React, { useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';
import { useHistory } from 'react-router-dom';
import { getEnumValuesAsArray } from 'utils/enum';
import titleCase from 'utils/title-case';
import { CreateCalibrationRequest } from 'views/bluviu-nutrition/features/calibrations/types/create-calibration-request.type';
import { useSelectedSite } from 'views/bluviu-nutrition/hooks/use-selected-site';
import { useCalibrationListState } from '../context/calibration-list-context';
import { useCreateCalibration } from '../hooks/use-create-calibration';
import { CalibrationModel } from '../types/calibration-model.enum';
import { getCalibrationType } from '../utils/get-calibration-type';

const CreateCalibrationDialog: React.FC<{}> = () => {
  const { createCalibration } = useCreateCalibration();
  const { selectedSite } = useSelectedSite();
  const history = useHistory();
  const intl = useIntl();
  const {
    calibrationsQuery: { data: calibrations },
  } = useCalibrationListState();

  const dialogRef = useRef<React.ElementRef<typeof Dialog>>(null);
  const [model, setModel] = useState<CalibrationModel>(CalibrationModel.EFFECTIVE_ENERGY_GT2_SIMPLE);
  const PAGE_NAME_ID = 'bluViuNutrition.calibrations.createCalibration';
  const invalidNames = calibrations?.map(({ name }) => name) ?? [];

  const { control, reset, handleSubmit, formState } = useForm<CreateCalibrationRequest>({
    defaultValues: {
      name: '',
      description: '',
    },
    mode: 'onChange',
  });

  const onSubmit = (data: CreateCalibrationRequest) => {
    createCalibration.mutate(
      {
        model: model,
        request: data,
      },
      {
        onSuccess: (calibration) => {
          reset();
          dialogRef.current?.setOpen(false);
          history.push(`/bluviu-nutrition/calibrations/${selectedSite?.id}/${getCalibrationType(model)}/${calibration.id}`);
        },
      }
    );
  };

  return (
    <Dialog
      ref={dialogRef}
      maxWidth={'xs'}
      title={`Create calibration`}
      button={
        <Button>
          <FormattedMessage id={`${PAGE_NAME_ID}.create`} />
        </Button>
      }>
      <Box display={'flex'} flexDirection={'column'} gap={4}>
        <Typography fontSize={14}>For site: {selectedSite?.name}</Typography>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display={'flex'} flexDirection={'column'} gap={4}>
            <TextField
              dark
              fullWidth
              controller={{
                control,
                name: 'name',
                rules: {
                  required: intl.formatMessage({ id: 'form.rules.required' }),
                  validate: (value) => (!invalidNames.includes(value) ? true : `The name must be unique`),
                },
              }}
              label={'Calibration name'}
            />
            <Select
              dark
              fullWidth
              value={model}
              onChange={(event) => setModel(event.target.value as CalibrationModel)}
              label={'Calibration model'}>
              {getEnumValuesAsArray(CalibrationModel).map((model, idx) => (
                <RadioSelectItem key={idx} value={model}>
                  {titleCase(model)}
                </RadioSelectItem>
              ))}
            </Select>
            <Box display={'flex'} flexDirection={'row'} justifyContent={'flex-end'}>
              <Button
                type="submit"
                disabled={createCalibration.isPending}
                startIcon={createCalibration.isPending ? <CircularProgress color="inherit" size={18} /> : null}>
                <FormattedMessage
                  id={`general.create`}
                  values={{
                    type: '',
                  }}
                />
              </Button>
            </Box>
          </Box>
        </form>
      </Box>
    </Dialog>
  );
};

export default React.memo(CreateCalibrationDialog);
